import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../utils/api';

export default function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
    
    // Auto-verify when all digits entered
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerify = async (code?: string) => {
    const codeToVerify = code || verificationCode.join('');
    
    if (codeToVerify.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    
    setIsVerifying(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: location.state?.email,
          code: codeToVerify,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsVerified(true);
        setTimeout(() => {
          navigate('/', { state: { showTour: true } });
        }, 2000);
      } else {
        setError(data.message || 'Invalid verification code. Please try again.');
        setVerificationCode(['', '', '', '', '', '']);
        document.getElementById('code-0')?.focus();
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: location.state?.email,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResendCooldown(60); // 60 second cooldown
        setError('');
      } else {
        setError(data.message || 'Failed to resend code. Please try again.');
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    }
  };

  if (isVerified) {
    return (
      <div className="auth-container">
        <div className="auth-content" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: '#DCFCE7', 
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircleIcon style={{ width: '48px', height: '48px', color: '#16A34A' }} />
          </div>
          
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>
            Email Verified!
          </h1>
          <p style={{ fontSize: '16px', color: '#6B7280', marginBottom: '32px' }}>
            Your email has been successfully verified. Redirecting you to complete your profile setup...
          </p>
          
          <div style={{ 
            display: 'inline-block',
            padding: '12px 24px',
            background: '#F3F4F6',
            borderRadius: '8px'
          }}>
            <ArrowPathIcon style={{ width: '20px', height: '20px', color: '#6B7280', animation: 'spin 1s linear infinite' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-content" style={{ maxWidth: '500px', margin: '0 auto' }}>
        {/* Logo */}
        <div className="auth-logo" style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img 
            src="/favicon.png" 
            alt="Certs Logo" 
            style={{ width: '64px', height: '64px', objectFit: 'contain', margin: '0 auto', display: 'block' }}
          />
        </div>

        {/* Icon */}
        <div style={{ 
          width: '64px', 
          height: '64px', 
          background: '#FFF7ED', 
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <EnvelopeIcon style={{ width: '32px', height: '32px', color: '#FF7A1A' }} />
        </div>

        {/* Header */}
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', textAlign: 'center', marginBottom: '12px' }}>
          Verify Your Email
        </h1>
        <p style={{ fontSize: '14px', color: '#6B7280', textAlign: 'center', marginBottom: '32px' }}>
          We've sent a 6-digit verification code to<br />
          <strong style={{ color: '#111827' }}>{email}</strong>
        </p>

        {/* Verification Code Input */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '12px', textAlign: 'center' }}>
            Enter Verification Code
          </label>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isVerifying}
                style={{
                  width: '48px',
                  height: '56px',
                  textAlign: 'center',
                  fontSize: '24px',
                  fontWeight: 600,
                  border: `2px solid ${error ? '#EF4444' : digit ? '#FF7A1A' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FF7A1A'}
                onBlur={(e) => e.target.style.borderColor = digit ? '#FF7A1A' : '#E5E7EB'}
              />
            ))}
          </div>
          {error && (
            <p style={{ color: '#EF4444', fontSize: '14px', marginTop: '12px', textAlign: 'center' }}>
              {error}
            </p>
          )}
        </div>

        {/* Verify Button */}
        <button
          onClick={() => handleVerify()}
          disabled={isVerifying || verificationCode.some(d => !d)}
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: '16px' }}
        >
          {isVerifying ? 'Verifying...' : 'Verify Email'}
        </button>

        {/* Resend Code */}
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
            Didn't receive the code?
          </p>
          <button
            onClick={handleResendCode}
            disabled={resendCooldown > 0}
            style={{
              background: 'none',
              border: 'none',
              color: resendCooldown > 0 ? '#9CA3AF' : '#FF7A1A',
              fontSize: '14px',
              fontWeight: 600,
              cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
              textDecoration: 'underline'
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
          </button>
        </div>

        {/* Help Text */}
        <div style={{ 
          marginTop: '32px', 
          padding: '16px', 
          background: '#F9FAFB', 
          borderRadius: '8px',
          border: '1px solid #E5E7EB'
        }}>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
            <strong style={{ color: '#374151' }}>💡 Tip:</strong> Check your spam folder if you don't see the email. 
            The code expires in 10 minutes.
          </p>
        </div>
      </div>
    </div>
  );
}
